import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Currencies } from 'src/app/models/Configurations/Currencies';
import { Languages } from 'src/app/models/Configurations/Languages';
import { ApiService } from '../api/api.service';
import { SessionService } from '../session/session.service';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {

  // expected: [{"code": "BRL", "name": "Real"}]
  private _languages: Languages[] = [];
  set setLanguages(languages: Languages[]) {
    this._languages = languages;
  }
  get languages(): Languages[] {
    return this._languages;
  }

  // expected: [{ "code": "BRL","name": "Real","symbol": "R$"}]
  private _currencies: Currencies[] = [];
  set setCurrencies(currencies: Currencies[]) {
    this._currencies = currencies;
  }
  get currencies(): Currencies[] {
    return this._currencies;
  }

  get languageUser(): string {
    return this.detectLanguageByLocale(this.sessionService.getDetails().language)
  }

  get currencyUser(): string {
    return this.sessionService.getDetails().currency;
  }

  constructor(private sessionService: SessionService,
    private api: ApiService,
    private snackBar: MatSnackBar) {
    this.getLanguages();
    this.getCurrencies();
  }

  detectLanguageByLocale(locale: string): string {
    return this.languages.find(x => x.code.includes(locale.slice(0, 2)))?.code ?? "";
  }

  detectCurrencyByLocale(locale: string): string {
    if (locale.includes("pt"))
      return "BRL";
    if (locale.includes("en"))
      return "USD";
    if (locale.includes("es"))
      return "PYG";

    return "";
  }

  formatNumberByCountry(value: number): string {
    if (!isNaN(value))
      return new Intl.NumberFormat(this.languageUser == "" ? "pt-BR" : this.languageUser,
        {
          style: 'currency',
          currency: this.currencyUser,
        })
        .format(value);
    return ""
  }

  formatNumberIntegerByCountry(value: number): string {
    if (!isNaN(value))
      return new Intl.NumberFormat(this.languageUser == "" ? "pt-BR" : this.languageUser)
        .format(value);
    return ""
  }

  formatNumberDecimalByCountry(value: number): string {
    if (!isNaN(value))
      return new Intl.NumberFormat(this.languageUser == "" ? "pt-BR" : this.languageUser, { minimumFractionDigits: 3 })
        .format(value);
    return ""
  }

  formatDateLocaleByCountry(date: Date): string {
    if (date) {
      const options: any = {
        year: 'numeric', month: 'numeric', day: 'numeric'
      } as const;
      return new Intl.DateTimeFormat(this.languageUser == "" ? "pt-BR" : this.languageUser, options).format(new Date(date));
    }
    return ""
  }

  formatTimeLocaleByCountry(date: Date): string {
    if (date) {
      const options: any = { hour: '2-digit', minute: '2-digit' } as const;
      return new Intl.DateTimeFormat(this.languageUser == "" ? "pt-BR" : this.languageUser, options).format(new Date(date));
    }
    return ""
  }

  formatDateTimeLocaleByCountry(date: Date): string {
    if (date) {
      const options = {
        hour12: false,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      } as const;
      return new Intl.DateTimeFormat([], options).format(new Date(date)).replace(",", "");
    }
    return "Date invalid"
  }

  formatDateWithHourLocaleByCountry(date: Date): string {
    if (date) {
      const options: any = {
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric'
      } as const;
      return new Intl.DateTimeFormat(this.languageUser == "" ? "pt-BR" : this.languageUser, options).format(new Date(date));
    }
    return ""
  }

  private async getLanguages(): Promise<Languages[]> {
    try {
      const r = await this.api.get<Languages[]>('/v1/configuration/languages');
      if (r.success) {
        this._languages = r.data;
        return r.data;
      }
      this.warningError("Não foi possível recuperar a linguagem");
      return [];
    } catch (err) {
      throw (err);
    }
  }

  private async getCurrencies(): Promise<Currencies[]> {
    try {
      const r = await this.api.get<Currencies[]>('/v1/configuration/currencies');
      if (r.success) {
        this._currencies = r.data;
        return r.data;
      }
      this.warningError("Não foi possível recuperar a moeda");
      return [];
    } catch (err) {
      throw (err);
    }
  }

  returnOnlyPhoneNumberWithoutDialCode(dialCode: string, phoneWithDialCode: string): string {
    return phoneWithDialCode.slice(dialCode.length + 1, phoneWithDialCode.length);
  }

  private warningError(message: string) {
    this.snackBar.open(message, '', {
      duration: 3000,
      verticalPosition: 'top',
      panelClass: ['warning-snackbar']
    });
  }
}
